import cloneDeep from 'clone-deep'
import { InputType, NewFileTableTypeOptions, NewInputTextOptions } from 'config'
import { useEffect, useMemo, useState } from 'react'
import { Modal } from 'stories/components'
import { capitalizeFirstLetter, InputConvert, InputValidate } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import {
  type CustomFieldInputType,
  type IServicingInfoCustomField,
  ServicingCustomFieldInputTypeOptions,
} from './types'

interface Props {
  /** Field being edited; pass null to add a new one. */
  field: IServicingInfoCustomField | null
  /** Existing field keys in the same section, used to enforce uniqueness. */
  existingKeys: string[]
  onClose: () => void
  onSave: (field: IServicingInfoCustomField) => void
}

/**
 * Column span options for the field's grid placement.
 * The Servicing Information page uses a 12-column grid.
 */
const spanOptions: Record<string, string> = {
  '2': '2 / 12',
  '3': '3 / 12',
  '4': '4 / 12',
  '6': '6 / 12',
  '8': '8 / 12',
  '12': '12 / 12 (full)',
}

const sanitizeKey = (title: string) =>
  title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || `field_${Date.now()}`

/**
 * Inputs shown in the modal. Mirrors the LoanFieldsProperties
 * VisiblePropertyModal pattern (RenderInput-driven) so the UX feels familiar.
 *
 * `visible` is mutated at render time based on the currently-selected
 * `inputType` so FileTable-specific fields only appear when relevant.
 */
const defaultInputs = (): Record<string, InputType> => ({
  title: {
    inputType: 'text',
    title: 'Title',
    required: true,
    value: '',
  },
  inputType: {
    inputType: 'Select',
    title: 'Input Component Type',
    options: ServicingCustomFieldInputTypeOptions as any,
    required: true,
    hasDefaultOption: true,
    value: 'Text',
  },
  // Sub-format for Text *and* FileTable. We swap its `options` in
  // `computedInputs` depending on which inputType is selected.
  type: {
    inputType: 'select',
    title: 'Input Format',
    options: NewInputTextOptions,
    required: false,
    hasDefaultOption: true,
    value: 'text',
    visible: false,
  },
  options: {
    inputType: 'SelectOptionInput',
    title: 'Select Options',
    value: [],
    required: true,
    visible: false,
  },
  /* ----- FileTable-only ----- */
  multiple: {
    inputType: 'ToggleButton',
    title: 'Allow Multiple Files',
    required: false,
    value: true,
    visible: false,
  },
  showStatus: {
    inputType: 'ToggleButton',
    title: 'Show Status Column',
    required: false,
    value: false,
    visible: false,
  },
  showCategory: {
    inputType: 'ToggleButton',
    title: 'Show Category Column',
    required: false,
    value: false,
    visible: false,
  },
  /* ----- Common trailers ----- */
  tooltip: {
    inputType: 'textarea',
    title: 'Tooltip',
    rows: 3,
    required: false,
    prefix: '',
    value: '',
  },
  span: {
    inputType: 'select',
    title: 'Column Span',
    options: spanOptions,
    hasDefaultOption: false,
    value: '4',
  },
  required: {
    inputType: 'ToggleButton',
    title: 'Required',
    required: false,
    value: false,
  },
})

export const CustomFieldModal = ({ field, existingKeys, onClose, onSave }: Props) => {
  const [inputs, setInputs] = useState<Record<string, InputType>>(defaultInputs())
  const [error, setError] = useState('')

  const isEdit = !!field

  useEffect(() => {
    const next = defaultInputs()
    if (field) {
      next.title.value = field.title
      // Legacy records may have inputType: 'ActionButton'; coerce those back to
      // 'Text' so the dropdown lands on a valid option.
      const incomingType = capitalizeFirstLetter(field.inputType || 'Text')
      const validTypes = Object.keys(ServicingCustomFieldInputTypeOptions)
      next.inputType.value = validTypes.includes(incomingType) ? incomingType : 'Text'
      next.type.value = field.type || (next.inputType.value === 'FileTable' ? '' : 'text')
      next.options.value = field.options || []
      next.tooltip.value = field.tooltip || ''
      next.span.value = field.span !== undefined ? `${field.span}` : '4'
      next.required.value = !!field.required
      // FileTable
      next.multiple.value = field.multiple !== false
      next.showStatus.value = !!field.showStatus
      next.showCategory.value = !!field.showCategory
    }
    setInputs(next)
    setError('')
  }, [field])

  const onChange = (key: string, value: any) => {
    const next = cloneDeep(inputs)
    if (next[key]) {
      value = InputConvert(next[key], value)
      next[key].value = value
      next[key].error = InputValidate({ ...next[key], value })
    }
    // Reset `type` when switching inputType so we don't carry the old Text format
    // into a FileTable (and vice-versa).
    if (key === 'inputType') {
      const it = String(value || '')
      if (it === 'FileTable') next.type.value = ''
      else if (it === 'Text') next.type.value = 'text'
    }
    setInputs(next)
  }

  /**
   * Recompute conditional visibility + options. Driven by the currently-selected
   * `inputType` so the modal only ever shows fields relevant to that component.
   */
  const computedInputs = useMemo(() => {
    const next = cloneDeep(inputs)
    const it = String(next.inputType.value || '')

    const isText = it === 'Text'
    const isSelect = it === 'Select'
    const isFileTable = it === 'FileTable'

    // `type` is reused for Text format AND FileTable format.
    next.type.visible = isText || isFileTable
    if (isFileTable) {
      next.type.title = 'Table Format'
      next.type.options = NewFileTableTypeOptions
      ;(next.type as any).hasDefaultOption = false
      next.type.required = false
    } else if (isText) {
      next.type.title = 'Input Format'
      next.type.options = NewInputTextOptions
      ;(next.type as any).hasDefaultOption = true
      next.type.required = true
    }

    next.options.visible = isSelect

    // FileTable-specific
    next.multiple.visible = isFileTable
    next.showStatus.visible = isFileTable
    next.showCategory.visible = isFileTable

    return next
  }, [inputs])

  const onSubmit = (): true | undefined => {
    const next = cloneDeep(computedInputs)
    let hasError = false
    Object.keys(next).forEach((key) => {
      if (next[key].visible === false) return
      next[key].error = InputValidate({ ...next[key], value: next[key].value })
      if (next[key].error?.length) hasError = true
    })
    setInputs(next)

    const title = String(next.title.value || '').trim()
    if (!title) {
      setError('Title is required')
      return
    }

    const inputType = String(next.inputType.value || 'Text') as CustomFieldInputType
    let options: any[] | undefined
    if (inputType === 'Select') {
      const opts = (next.options.value as any[]) || []
      const validOpts = opts.filter((o) => String(o?.title || '').trim().length > 0)
      if (!validOpts.length) {
        setError('At least one Select option is required')
        return
      }
      const seen = new Set<string>()
      for (const o of validOpts) {
        if (seen.has(o.key)) {
          setError('Duplicate option keys are not allowed')
          return
        }
        seen.add(o.key)
      }
      options = validOpts
    }

    if (hasError) return

    const key =
      field?.key ||
      (() => {
        let candidate = `cf_${sanitizeKey(title)}`
        let i = 1
        while (existingKeys.includes(candidate)) {
          candidate = `cf_${sanitizeKey(title)}_${i++}`
        }
        return candidate
      })()

    const saved: IServicingInfoCustomField = {
      key,
      title,
      inputType,
      span: Number(next.span.value || 4),
      required: !!next.required.value,
    }

    if (inputType === 'Text') {
      saved.type = String(next.type.value || 'text')
    }
    if (inputType === 'FileTable') {
      const fmt = String(next.type.value || '')
      if (fmt) saved.type = fmt
      saved.multiple = next.multiple.value !== false
      saved.showStatus = !!next.showStatus.value
      saved.showCategory = !!next.showCategory.value
    }
    if (options) saved.options = options
    const tooltip = String(next.tooltip.value || '').trim()
    if (tooltip) saved.tooltip = tooltip

    onSave(saved)
    return true
  }

  return (
    <Modal
      isOpen
      title={isEdit ? 'Edit Custom Field' : 'New Custom Field'}
      titleClass="w-144"
      titleOkay="Submit"
      onClose={onClose}
      onOk={onSubmit}
      lastUpdatedAt={Date.now()}
    >
      <div className="w-144">
        {Object.keys(computedInputs).map((key) => {
          const input = computedInputs[key]
          if (input.visible === false) return null
          return (
            <div key={key} className="mb-2">
              <RenderInput input={input} Key={key} onChange={onChange} />
            </div>
          )
        })}
        {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
      </div>
    </Modal>
  )
}
